import { useCallback } from 'react';
import { FetchResult, useMutation } from '@apollo/client';
import type {
  SubmitReportMutation,
  SubmitReportMutationVariables,
  SubmitPrelimReportMutation,
  SubmitPrelimReportMutationVariables,
  CancelReportMutation,
  CancelReportMutationVariables,
  CancelPrelimReportMutation,
  CancelPrelimReportMutationVariables,
  SubmitReportInvalidInput,
  SubmitReportProblem,
} from 'generated/graphql';
import type { SetterOrUpdater } from 'recoil';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { logger } from 'modules/logger';
import { unreachableCaseError } from 'types';
import { ReporterSaveState } from 'config/recoilState';
import { CrossWindowDataRefetchType } from 'modules/EventsManager';
import { broadcastCaseAndWorklistRefresh } from 'common/CurrentCase/broadcastCaseAndWorklistRefresh';
import { type ToastKey } from 'common/ui/Toaster/Toast';
import { useToasterDispatch } from 'common/ui/Toaster';
import type { LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { ReportExitAction } from 'domains/reporter/RichTextEditor/analytics/performance';
import type { HandleAutoloadNextCaseParams } from 'hooks/useWorklistAutoLoad';
import { getReportSMID, getCaseInfo } from './utils';
import { ReportStatus } from '../state';
import type { ReportStatuses } from '../state';
import type { SlateContent } from '../types';
import { SubmitType, type OnSubmitParams } from './types';
import type { SubmissionTarget } from './useSubmissionWorkstationID';
import { isSubmissionInFlight } from './reportStatusGuards';
import { findBlockingPlaceholders, findEnforcedEmptyRequiredFields } from './reportValidation';
import {
  enforceRequiredFields,
  notifySubmissionCancelled,
  notifyWorkstationPatchFailure,
} from './submitGuards';
import { emitReportExitActionEnd } from './reportExitActionEffects';
import { useAutoSaveReport, type FieldsUpdateProps } from './useAutoSaveReport';

type UseAutoSaveReportReturn = ReturnType<typeof useAutoSaveReport>;
type ToasterDispatch = ReturnType<typeof useToasterDispatch>;

const DEFAULT_TOAST_OPTIONS = {
  severity: 'error',
  position: 'bottom-right',
} as const;

/**
 * Owns the report submit workflow: the shared `handleSubmit` (guards + flush + report/Idle case) and
 * `handleCancelSubmit`. The addendum submit and cancel branches delegate to useAddendumWorkflow
 * (`submitAddendum` / `cancelAddendumSubmit`); required-field/workstation guards come from
 * submitGuards so report and addendum paths share them without cross-hook injection.
 */
export interface UseSubmitReportParams {
  isReporterSubmissionEnabled: boolean;
  isPrelimSubmissionEnabled: boolean;
  autoloadTemplateState: AutoloadTemplateState | undefined;
  savingState: UseAutoSaveReportReturn['savingState'];
  setSavingState: UseAutoSaveReportReturn['setSavingState'];
  fieldsUpdate: UseAutoSaveReportReturn['fieldsUpdate'];
  fieldsUpdateDebounced: UseAutoSaveReportReturn['fieldsUpdateDebounced'];
  addendumUpdateDebounced: UseAutoSaveReportReturn['addendumUpdateDebounced'];
  hasHeadingError: boolean;
  hasReportContentChanged: boolean;
  setHasReportContentChanged: (value: boolean) => void;
  worklistItem: LoadedReportWorklistItem;
  status: ReportStatuses;
  setStatus: (status: ReportStatuses) => void;
  getCurrentReportContent: () => SlateContent;
  getCurrentReportUpdateType: () => FieldsUpdateProps['updateType'];
  activateTemplateID: string | undefined;
  isRecording: boolean;
  stopRecording: () => Promise<void>;
  placeholderFieldDialogState: { open: boolean };
  setPlaceholderFieldDialogState: (value: {
    emptyPlaceholders: ReturnType<typeof findBlockingPlaceholders>;
    open: boolean;
  }) => void;
  isEmptyRequiredFieldsDialogOpenForCurrentCase: boolean;
  openEmptyRequiredFieldsDialog: (
    emptyRequiredFields: ReturnType<typeof findEnforcedEmptyRequiredFields>
  ) => void;
  setAllRequiredFieldsTouched: () => void;
  resolveSubmissionWorkstationID: (target: SubmissionTarget) => Promise<{
    cancelled: boolean;
    workstationIDToSubmit: string | null;
    requiresPatchBeforeSubmit: boolean;
  }>;
  workstationID: Nullable<string>;
  isBypassAndSign: boolean;
  isBypassAndSignPrelim: boolean;
  submitReport: ReturnType<
    typeof useMutation<SubmitReportMutation, SubmitReportMutationVariables>
  >[0];
  submitPrelimReport: ReturnType<
    typeof useMutation<SubmitPrelimReportMutation, SubmitPrelimReportMutationVariables>
  >[0];
  analyticsData: Record<string, unknown> | undefined;
  enqueueToast: ToasterDispatch['enqueueToast'];
  enqueueOrUpdateToast: ToasterDispatch['enqueueOrUpdateToast'];
  latestToastKey: Nullable<ToastKey>;
  setLatestToastKey: (key: Nullable<ToastKey>) => void;
  trackTypedCharacters: () => void;
  setPrelimReportWorklistSmid: SetterOrUpdater<string | null>;
  startCountdown: () => void;
  measureReportExitActionEndWithMetadata: (metadata: { exitAction: ReportExitAction }) => void;
  broadcastRefreshCase: () => Promise<unknown>;
  maybeAutoloadNextCaseCallback: (
    isWorklistAutoLoadEnabled: boolean,
    defaultToastMsg: string,
    params: HandleAutoloadNextCaseParams
  ) => Promise<void>;
  isWorklistAutoLoadEnabled: boolean;
  setRightDrawerCatalogRefreshSequence: (updater: (previous: number) => number) => void;
  // report cancel deps
  cancelCountdown: () => void;
  cancelReport: ReturnType<
    typeof useMutation<CancelReportMutation, CancelReportMutationVariables>
  >[0];
  cancelPrelimReport: ReturnType<
    typeof useMutation<CancelPrelimReportMutation, CancelPrelimReportMutationVariables>
  >[0];
  // addendum delegation (useAddendumWorkflow)
  submitAddendum: (options?: OnSubmitParams) => Promise<void>;
  cancelAddendumSubmit: () => Promise<void>;
}

export const useSubmitReport = ({
  isReporterSubmissionEnabled,
  isPrelimSubmissionEnabled,
  autoloadTemplateState,
  savingState,
  setSavingState,
  fieldsUpdate,
  fieldsUpdateDebounced,
  addendumUpdateDebounced,
  hasHeadingError,
  hasReportContentChanged,
  setHasReportContentChanged,
  worklistItem,
  status,
  setStatus,
  getCurrentReportContent,
  getCurrentReportUpdateType,
  activateTemplateID,
  isRecording,
  stopRecording,
  placeholderFieldDialogState,
  setPlaceholderFieldDialogState,
  isEmptyRequiredFieldsDialogOpenForCurrentCase,
  openEmptyRequiredFieldsDialog,
  setAllRequiredFieldsTouched,
  resolveSubmissionWorkstationID,
  workstationID,
  isBypassAndSign,
  isBypassAndSignPrelim,
  submitReport,
  submitPrelimReport,
  analyticsData,
  enqueueToast,
  enqueueOrUpdateToast,
  latestToastKey,
  setLatestToastKey,
  trackTypedCharacters,
  setPrelimReportWorklistSmid,
  startCountdown,
  measureReportExitActionEndWithMetadata,
  broadcastRefreshCase,
  maybeAutoloadNextCaseCallback,
  isWorklistAutoLoadEnabled,
  setRightDrawerCatalogRefreshSequence,
  cancelCountdown,
  cancelReport,
  cancelPrelimReport,
  submitAddendum,
  cancelAddendumSubmit,
}: UseSubmitReportParams) => {
  const handleCancelSubmit = useCallback(async () => {
    if (status === ReportStatus.ProvisionalSubmit) {
      const reportSmid = getReportSMID(worklistItem);

      cancelCountdown();
      setStatus(ReportStatus.Cancelling);
      await cancelReport({
        variables: { smid: reportSmid },
      });
      setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
      setStatus(ReportStatus.Idle);
      analytics.track(reporter.usr.submitReportCancel, {
        ...analyticsData,
        smid: reportSmid,
      });
    } else if (status === ReportStatus.ProvisionalPrelimSubmit) {
      const reportSmid = getReportSMID(worklistItem);

      cancelCountdown();
      setStatus(ReportStatus.CancellingPrelim);
      await cancelPrelimReport({
        variables: { smid: reportSmid },
      });
      setStatus(ReportStatus.Idle);
      analytics.track(reporter.usr.submitPrelimReportCancel, {
        ...analyticsData,
        smid: reportSmid,
      });
    } else if (status === ReportStatus.ProvisionalAddendumSubmit) {
      await cancelAddendumSubmit();
    }
  }, [
    status,
    worklistItem,
    cancelCountdown,
    setStatus,
    cancelReport,
    cancelPrelimReport,
    analyticsData,
    cancelAddendumSubmit,
    setRightDrawerCatalogRefreshSequence,
  ]);

  const handleSubmit: (options?: OnSubmitParams) => Promise<void> = useCallback(
    async (options) => {
      const isPrelim = options?.type === SubmitType.Prelim;
      if (!isReporterSubmissionEnabled || (!isPrelimSubmissionEnabled && isPrelim)) {
        logger.warn(
          '[useSubmitReport] Attempted to sign but the user does not have the feature enabled',
          {
            reportSmid: getReportSMID(worklistItem),
            status,
          }
        );
        return;
      }

      if (autoloadTemplateState === 'pending') {
        logger.warn(
          '[useSubmitReport] Attempted to sign while template autoload is still pending',
          {
            reportSmid: getReportSMID(worklistItem),
            status,
          }
        );
        options?.onError && options?.onError();
        return;
      }

      if (options?.force !== true && savingState === ReporterSaveState.Saving) {
        logger.warn('[useSubmitReport] Attempted to sign report without saving latest changes', {
          reportSmid: getReportSMID(worklistItem),
        });
        enqueueToast('Please wait until your changes are saved before signing the report.', {
          icon: 'yellowWarning',
        });
        return;
      }

      if (hasHeadingError === true) {
        logger.warn('[useSubmitReport] Attempted to sign report with duplicate section headers', {
          reportSmid: getReportSMID(worklistItem),
          submitType: isPrelim ? SubmitType.Prelim : SubmitType.Final,
        });
        options?.onError && options?.onError();
        return;
      }

      if (
        options?.force !== true &&
        (placeholderFieldDialogState.open === true ||
          isEmptyRequiredFieldsDialogOpenForCurrentCase ||
          isSubmissionInFlight(status))
      ) {
        logger.warn('[useSubmitReport] Attempted to sign report in an invalid state', {
          reportSmid: getReportSMID(worklistItem),
          status,
          placeholderFieldDialogOpen: placeholderFieldDialogState.open,
          emptyRequiredFieldsDialogStateOpen: isEmptyRequiredFieldsDialogOpenForCurrentCase,
        });

        return;
      }

      logger.debug('[FieldsProvider] Initiating report submission process...');

      await fieldsUpdateDebounced.flush();
      await addendumUpdateDebounced.flush();

      const reportSmid = getReportSMID(worklistItem);

      switch (status) {
        // No-op, this is the default state before we've interpreted the countdown state
        case ReportStatus.Init:
          return;
        case ReportStatus.Idle: {
          // When the Submit button is pressed, stop the dictation
          if (isRecording === true) {
            await stopRecording();
          }

          const content = getCurrentReportContent();

          if (options?.force !== true && placeholderFieldDialogState.open === false) {
            const emptyPlaceholders = findBlockingPlaceholders({ content, isPrelim });

            if (emptyPlaceholders.length > 0) {
              setPlaceholderFieldDialogState({
                emptyPlaceholders,
                open: true,
              });
              options?.onError && options?.onError();
              return;
            }
          }

          if (
            enforceRequiredFields({
              content,
              isPrelim,
              isEmptyRequiredFieldsDialogOpen: isEmptyRequiredFieldsDialogOpenForCurrentCase,
              openEmptyRequiredFieldsDialog,
              setAllRequiredFieldsTouched,
            })
          ) {
            options?.onError && options?.onError();
            return;
          }

          const reportWorkstationSubmission = !isPrelim
            ? await resolveSubmissionWorkstationID('report')
            : {
                cancelled: false,
                workstationIDToSubmit: workstationID ?? null,
                requiresPatchBeforeSubmit: false,
              };

          if (reportWorkstationSubmission.cancelled) {
            notifySubmissionCancelled({
              enqueueToast,
              target: 'report',
              onError: options?.onError,
            });
            return;
          }

          /* If there is no saved report and the editor content has not changed,
             the user is signing a report that is simply an unchanged template.
             In this case, we need to save the report, since no autosave will have yet happened. */
          const report = worklistItem.report;
          const created = report?.created;
          const sectionsLastUpdated = report?.sectionsLastUpdated;
          const hasSavedReportContent =
            created != null && sectionsLastUpdated != null && sectionsLastUpdated !== created;
          const shouldSaveReportBeforeSubmit = !hasReportContentChanged && !hasSavedReportContent;
          const needsPreSubmitReportUpdate =
            shouldSaveReportBeforeSubmit || reportWorkstationSubmission.requiresPatchBeforeSubmit;

          if (needsPreSubmitReportUpdate) {
            if (shouldSaveReportBeforeSubmit) {
              logger.info('[useSubmitReport] Saving an unchanged report before signing ...', {
                reportSmid: getReportSMID(worklistItem),
              });
            }

            setSavingState(ReporterSaveState.Saving);
            const didUpdateReport = await fieldsUpdate({
              contentToSubmit: content,
              updateType: getCurrentReportUpdateType(),
              activateTemplateIDToSubmit: activateTemplateID,
              workstationIDToSubmit: reportWorkstationSubmission.workstationIDToSubmit,
              errorMode: reportWorkstationSubmission.requiresPatchBeforeSubmit
                ? 'submissionWorkstationPatch'
                : 'default',
            });

            if (reportWorkstationSubmission.requiresPatchBeforeSubmit && didUpdateReport !== true) {
              notifyWorkstationPatchFailure({
                enqueueToast,
                target: 'report',
                onError: options?.onError,
              });
              return;
            }
          }

          // Now, submit the report.
          let statusToSet: ReportStatuses = ReportStatus.Submitting;
          let isBypass = isBypassAndSign;
          let submitType = SubmitType.Final;

          if (isPrelim) {
            statusToSet = ReportStatus.SubmittingPrelim;
            isBypass = isBypassAndSignPrelim;
            submitType = SubmitType.Prelim;
          }

          setStatus(statusToSet);

          let submitResult:
            | FetchResult<SubmitPrelimReportMutation>
            | FetchResult<SubmitReportMutation>
            | undefined;
          let errors: any;

          try {
            logger.info(
              `[useSubmitReport] Attempting to submit ${submitType} report with bypass ${isBypass === true ? 'is' : 'is not'} enabled`
            );
            const variables = {
              smid: reportSmid,
              immediate_submit: isBypass,
            };
            submitResult = isPrelim
              ? await submitPrelimReport({ variables })
              : await submitReport({ variables });
            errors = submitResult?.errors;
          } catch (err: any) {
            errors = [err];
            logger.error(
              `[useSubmitReport] Error occurred when submitting ${submitType} report`,
              {
                smid: reportSmid,
              },
              err
            );
          }

          analytics.track(isPrelim ? reporter.usr.submitPrelimReport : reporter.usr.submitReport, {
            ...analyticsData,
            smid: reportSmid,
          });

          if (!submitResult?.data || errors?.length) {
            setStatus(ReportStatus.Idle);
            const toastKey = enqueueOrUpdateToast(
              `Report ${reportSmid} was not signed, an error occurred.`,
              latestToastKey,
              DEFAULT_TOAST_OPTIONS
            );
            setLatestToastKey(toastKey);
            options?.onError && options?.onError();
            return;
          }

          // Handle submit problems
          const submitResultData: SubmitPrelimReportMutation | SubmitReportMutation | undefined =
            submitResult.data;
          let invalidInputs: SubmitReportInvalidInput[] = [];
          let message = '';

          const isSubmitReportProblem =
            (submitResultData as SubmitReportMutation)?.submitReport?.__typename ===
            'SubmitReportProblem';
          const isSubmitPrelimProblem =
            (submitResultData as SubmitPrelimReportMutation)?.submitPrelimReport?.__typename ===
            'SubmitPrelimReportProblem';

          if (isSubmitPrelimProblem) {
            message = `Preliminary report ${reportSmid} was not signed, an error occurred.`;
            const toastKey = enqueueOrUpdateToast(message, latestToastKey, DEFAULT_TOAST_OPTIONS);
            setLatestToastKey(toastKey);
          } else if (isSubmitReportProblem) {
            message = `Report ${reportSmid} was not signed, an error occurred.`;
            invalidInputs = [
              ...((
                (submitResultData as SubmitReportMutation)
                  .submitReport as unknown as SubmitReportProblem
              )?.invalidInputs?.filter(
                (input): input is SubmitReportInvalidInput => input != null
              ) ?? []),
            ];
            // Validating fields
            if (invalidInputs.length) {
              invalidInputs.forEach((input) => {
                if (input != null) {
                  let inputMessage: string = input.message;
                  if (inputMessage.includes('Impression section is empty.')) {
                    inputMessage += " Please make sure 'Impression:' is formatted as an H1 header.";
                  }
                  const toastKey = enqueueOrUpdateToast(
                    inputMessage,
                    latestToastKey,
                    DEFAULT_TOAST_OPTIONS
                  );
                  logger.warn(
                    '[useSubmitReport] - "SubmitReportProblem" upon attempted submission - ',
                    inputMessage,
                    { input }
                  );
                  setLatestToastKey(toastKey);
                }
              });
            } else {
              const toastKey = enqueueOrUpdateToast(message, latestToastKey, DEFAULT_TOAST_OPTIONS);
              setLatestToastKey(toastKey);
            }
          }
          if (isSubmitPrelimProblem || isSubmitReportProblem) {
            setStatus(ReportStatus.Idle);
            return;
          }

          // If we reach this point, the report was successfully submitted
          trackTypedCharacters();

          setHasReportContentChanged(false);

          if (!isPrelim && isBypassAndSign) {
            setStatus(ReportStatus.Submitted);
          } else if (isPrelim && isBypassAndSignPrelim) {
            setStatus(ReportStatus.Idle);
            setPrelimReportWorklistSmid(worklistItem.smid);
          } else {
            startCountdown();
            setStatus(
              isPrelim ? ReportStatus.ProvisionalPrelimSubmit : ReportStatus.ProvisionalSubmit
            );
          }

          let exitAction: ReportExitAction;
          if (isBypass && isPrelim) {
            exitAction = ReportExitAction.BypassPrelim;
          } else if (isBypass) {
            exitAction = ReportExitAction.BypassSign;
          } else if (isPrelim) {
            exitAction = ReportExitAction.Prelim;
          } else {
            exitAction = ReportExitAction.Sign;
          }

          emitReportExitActionEnd(measureReportExitActionEndWithMetadata, exitAction);

          const defaultToastMsg = isPrelim
            ? `Report for ${getCaseInfo(worklistItem)} has been marked as preliminary.`
            : `Report for ${getCaseInfo(worklistItem)} has been signed.`;

          await broadcastCaseAndWorklistRefresh(
            broadcastRefreshCase,
            CrossWindowDataRefetchType.CLAIMED_ITEMS
          );

          await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
            reportExitAction: exitAction,
            defaultToastMsg,
            isAddendum: false,
            onAutoloadComplete: (nextCaseSmid) => {
              if (nextCaseSmid != null) {
                setPrelimReportWorklistSmid(null);
              }
            },
          });
          if (!isPrelim) {
            setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
          }

          // If the user was closing the window, we want to close it now that the report has been signed
          options?.onSuccess && options.onSuccess();
          return;
        }
        // No-op, this is when the user is submitting a request to the server
        case ReportStatus.Submitting:
        case ReportStatus.SubmittingPrelim:
          return;
        case ReportStatus.ProvisionalSubmit:
        case ReportStatus.ProvisionalPrelimSubmit:
          return;
        // No-op, state cannot be changed while 'cancelling'
        case ReportStatus.Cancelling:
        case ReportStatus.CancellingPrelim:
          return;
        // No-op, button is disabled. Users add an addendum with a different button
        case ReportStatus.Submitted:
          return;
        case ReportStatus.AddendumAdd:
          await submitAddendum(options);
          return;
        case ReportStatus.AddendumSubmitting:
          return;
        case ReportStatus.ProvisionalAddendumSubmit:
          return;
        case ReportStatus.AddendumCancelling:
          return;
        default:
          return unreachableCaseError(status);
      }
    },
    [
      isReporterSubmissionEnabled,
      isPrelimSubmissionEnabled,
      autoloadTemplateState,
      savingState,
      hasHeadingError,
      placeholderFieldDialogState.open,
      isEmptyRequiredFieldsDialogOpenForCurrentCase,
      status,
      fieldsUpdateDebounced,
      addendumUpdateDebounced,
      worklistItem,
      enqueueToast,
      openEmptyRequiredFieldsDialog,
      setAllRequiredFieldsTouched,
      isRecording,
      setStatus,
      analyticsData,
      trackTypedCharacters,
      isBypassAndSign,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      getCurrentReportContent,
      getCurrentReportUpdateType,
      hasReportContentChanged,
      setHasReportContentChanged,
      isBypassAndSignPrelim,
      measureReportExitActionEndWithMetadata,
      stopRecording,
      setPlaceholderFieldDialogState,
      setSavingState,
      fieldsUpdate,
      activateTemplateID,
      submitPrelimReport,
      submitReport,
      broadcastRefreshCase,
      enqueueOrUpdateToast,
      latestToastKey,
      setLatestToastKey,
      setPrelimReportWorklistSmid,
      setRightDrawerCatalogRefreshSequence,
      startCountdown,
      resolveSubmissionWorkstationID,
      workstationID,
      submitAddendum,
    ]
  );

  return { handleSubmit, handleCancelSubmit };
};
